import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Plus, Warehouse as WarehouseIcon, ArrowRight, Repeat, X, AlertTriangle, Search, Edit, CheckCircle, XCircle, Clock, User, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { trNorm } from '@/lib/arama';
import { trSayi } from '@/lib/sayi';

const whTypes = { main: 'Ana Depo', kitchen: 'Mutfak', bar: 'Bar', cold_storage: 'Soguk Depo', other: 'Diger' };
// Yalnız GÖSTERİM: taşeron deposu taşeron açılınca kendiliğinden açılır
// (insaat.py). Seçim listesinde yok — elle açılsa taşeronla bağı olmazdı.
const whTypeLabels = { ...whTypes, taseron: 'Alt Yüklenici' };

const transferStatusLabels = {
  pending_warehouse: 'Depo Onayi Bekliyor',
  pending_manager: 'Yonetici Onayi Bekliyor',
  completed: 'Tamamlandi',
  rejected: 'Reddedildi',
  draft: 'Taslak'
};

const transferStatusColors = {
  pending_warehouse: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  pending_manager: 'bg-[#2e2d29]/10 text-[#2e2d29]',
  completed: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  rejected: 'bg-[#7a3535]/10 text-[#7a3535]',
  draft: 'bg-[#e8dfd0] text-[#3F3E39]'
};

export default function WarehouseList() {
  return (
    <div data-testid="warehouse-list-page">
      <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight mb-6">Depolar</h1>
      <Tabs defaultValue="warehouses" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="warehouses">Depolar</TabsTrigger>
          <TabsTrigger value="transfers">Depolar Arasi Transfer</TabsTrigger>
        </TabsList>

        <TabsContent value="warehouses">
          <WarehousesTab />
        </TabsContent>
        <TabsContent value="transfers">
          <TransfersTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── WAREHOUSES TAB ───
function WarehousesTab() {
  const [warehouses, setWarehouses] = useState([]);
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingWh, setEditingWh] = useState(null);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('main');
  const [newResponsibleUsers, setNewResponsibleUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { 
    fetchWarehouses(); 
    fetchUsers();
  }, []);

  const fetchWarehouses = async () => {
    try {
      const res = await api.get('/warehouses');
      setWarehouses(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data || []);
    } catch (err) { console.error(err); }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    try {
      await api.post('/warehouses', { 
        name: newName, 
        type: newType,
        responsible_users: newResponsibleUsers
      });
      toast.success('Depo olusturuldu');
      setShowNew(false);
      resetForm();
      fetchWarehouses();
    } catch (err) { toast.error('Hata olustu'); }
    setLoading(false);
  };

  const handleEdit = (wh) => {
    setEditingWh(wh);
    setNewName(wh.name);
    setNewType(wh.type || 'main');
    setNewResponsibleUsers(wh.responsible_users || []);
    setShowEdit(true);
  };

  const handleUpdate = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    try {
      await api.patch(`/warehouses/${editingWh.id}`, {
        name: newName,
        type: newType,
        responsible_users: newResponsibleUsers
      });
      toast.success('Depo guncellendi');
      setShowEdit(false);
      resetForm();
      fetchWarehouses();
    } catch (err) { toast.error('Hata olustu'); }
    setLoading(false);
  };

  const resetForm = () => {
    setNewName('');
    setNewType('main');
    setNewResponsibleUsers([]);
    setEditingWh(null);
  };

  const toggleResponsibleUser = (userId) => {
    setNewResponsibleUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#8a7e70]">{warehouses.length} depo</p>
        <Button onClick={() => { resetForm(); setShowNew(true); }}
          className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="new-warehouse-btn">
          <Plus size={16} className="mr-2" /> Yeni Depo
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {warehouses.map((wh) => (
          <Card key={wh.id} className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm erp-card group" data-testid={`warehouse-card-${wh.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#2e2d29]/10 flex items-center justify-center">
                  <WarehouseIcon size={24} strokeWidth={1.5} className="text-[#2e2d29]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <Link to={`/erp/warehouses/${wh.id}`} className="hover:underline">
                      <h3 className="text-lg font-bold text-[#1a1916]">{wh.name}</h3>
                    </Link>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(wh)} 
                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`edit-warehouse-${wh.id}`}>
                      <Edit size={14} />
                    </Button>
                  </div>
                  <span className="status-badge bg-[#e8dfd0] text-[#3F3E39] mt-1">{whTypeLabels[wh.type] || wh.type}</span>
                  <div className="mt-3">
                    <p className="text-xs text-[#8a7e70]">Urun Cesidi</p>
                    <p className="text-2xl font-bold text-[#1a1916]">{wh.total_items}</p>
                  </div>
                  {(wh.responsible_user_names || []).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#e8dfd0]">
                      <p className="text-xs text-[#8a7e70] flex items-center gap-1 mb-1">
                        <Users size={12} /> Depo Sorumluları
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {wh.responsible_user_names.map((name, i) => (
                          <Badge key={i} variant="secondary" className="text-xs bg-[#3d6e4e]/10 text-[#3d6e4e]">
                            {name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* New Warehouse Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Yeni Depo</DialogTitle>
            <DialogDescription className="text-[#8a7e70] text-sm">
              Depo bilgilerini girin ve sorumlu kullanicilar atayın.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Depo Adi *</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                className="bg-white border-[#d0c4b0]" data-testid="new-warehouse-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Depo Turu</Label>
              <Select value={newType} onValueChange={setNewType}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="new-warehouse-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(whTypes).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] flex items-center gap-2">
                <Users size={14} /> Depo Sorumluları
              </Label>
              <div className="border border-[#d0c4b0] rounded-lg bg-white max-h-48 overflow-y-auto">
                {users.length === 0 ? (
                  <p className="p-3 text-sm text-[#8a7e70]">Kullanıcı bulunamadı</p>
                ) : (
                  users.map(u => (
                    <div key={u.id} 
                      className="flex items-center gap-2 p-2 hover:bg-[#f5f0e8] cursor-pointer border-b border-[#e8dfd0] last:border-b-0"
                      onClick={() => toggleResponsibleUser(u.id)}>
                      <Checkbox checked={newResponsibleUsers.includes(u.id)} />
                      <span className="text-sm text-[#1a1916]">{u.name}</span>
                      <span className="text-xs text-[#8a7e70]">({u.email})</span>
                    </div>
                  ))
                )}
              </div>
              {newResponsibleUsers.length > 0 && (
                <p className="text-xs text-[#3d6e4e]">{newResponsibleUsers.length} kisi secildi</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} className="border-[#d0c4b0]">Iptal</Button>
            <Button onClick={handleCreate} disabled={loading}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="save-warehouse-btn">
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Warehouse Dialog */}
      <Dialog open={showEdit} onOpenChange={(open) => { if (!open) resetForm(); setShowEdit(open); }}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Depo Duzenle</DialogTitle>
            <DialogDescription className="text-[#8a7e70] text-sm">
              Depo bilgilerini guncelleyin ve sorumlu kullanicilari atayın.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Depo Adi *</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                className="bg-white border-[#d0c4b0]" data-testid="edit-warehouse-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Depo Turu</Label>
              <Select value={newType} onValueChange={setNewType}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="edit-warehouse-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(whTypes).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] flex items-center gap-2">
                <Users size={14} /> Depo Sorumluları
              </Label>
              <div className="border border-[#d0c4b0] rounded-lg bg-white max-h-48 overflow-y-auto">
                {users.length === 0 ? (
                  <p className="p-3 text-sm text-[#8a7e70]">Kullanıcı bulunamadı</p>
                ) : (
                  users.map(u => (
                    <div key={u.id} 
                      className="flex items-center gap-2 p-2 hover:bg-[#f5f0e8] cursor-pointer border-b border-[#e8dfd0] last:border-b-0"
                      onClick={() => toggleResponsibleUser(u.id)}>
                      <Checkbox checked={newResponsibleUsers.includes(u.id)} />
                      <span className="text-sm text-[#1a1916]">{u.name}</span>
                      <span className="text-xs text-[#8a7e70]">({u.email})</span>
                    </div>
                  ))
                )}
              </div>
              {newResponsibleUsers.length > 0 && (
                <p className="text-xs text-[#3d6e4e]">{newResponsibleUsers.length} kisi secildi</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowEdit(false); }} className="border-[#d0c4b0]">Iptal</Button>
            <Button onClick={handleUpdate} disabled={loading}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="update-warehouse-btn">
              {loading ? 'Kaydediliyor...' : 'Guncelle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── TRANSFERS TAB ───
function TransfersTab() {
  // TRANSFER MİKTARI HERKESE GÖRÜNÜR (kullanıcı, 2026-09-29). Bir dönem
  // `stock_quantity` iznine bağlanıp "gizli" yazıyordu: onaylayacak depo
  // sorumlusu NEYİ onayladığını göremiyordu. Gizlenecek olan DEPO BAKİYESİdir
  // (stok miktarı), transfer edilen miktar değil — o, onaylanan işlemin
  // kendisi. Bakiye tarafı sunucuda gizli (`available_at_creation`).
  const { user } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchTransfers();
  }, []);

  const fetchTransfers = () => {
    api.get('/transfers').then(r => setTransfers(r.data)).catch(() => {});
  };

  const handleApproveWarehouse = async (transferId) => {
    setActionLoading(true);
    try {
      await api.post(`/transfers/${transferId}/approve-warehouse`);
      toast.success('Transfer onaylandi ve tamamlandi');
      fetchTransfers();
      setShowDetail(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata olustu');
    }
    setActionLoading(false);
  };

  const handleReject = async () => {
    if (!selectedTransfer) return;
    setActionLoading(true);
    try {
      await api.post(`/transfers/${selectedTransfer.id}/reject`, { reason: rejectReason });
      toast.success('Transfer reddedildi');
      setShowRejectDialog(false);
      setRejectReason('');
      fetchTransfers();
      setShowDetail(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata olustu');
    }
    setActionLoading(false);
  };

  const openDetail = (t) => {
    setSelectedTransfer(t);
    setShowDetail(true);
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#8a7e70]">{transfers.length} transfer</p>
        <Button onClick={() => setShowNew(true)}
          className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="new-transfer-btn">
          <Plus size={16} className="mr-2" /> Yeni Transfer
        </Button>
      </div>

      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tarih</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Cikis Deposu</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase"></TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Giris Deposu</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Urun Cesidi</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Kaydeden</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Islemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transfers.map((t) => (
              <TableRow key={t.id} className="erp-table-row border-[#d0c4b0] cursor-pointer hover:bg-[#f5f0e8]" 
                onClick={() => openDetail(t)}>
                {/* İŞ tarihi gösterilir, kayıt anı değil — stok hareketi de bu
                    tarihe yazılıyor. Burada `created_at` duruyordu: geçmiş
                    tarihli girilen transferlerin hepsi "bugün" görünüyor,
                    kullanıcı verinin değiştiğini sanıyordu. Eski kayıtlarda
                    `transfer_date` yoksa kayıt anına düşülür.
                    (TransferList.js'te aynı desen zaten doğruydu.) */}
                <TableCell className="text-sm text-[#3F3E39]">{new Date(t.transfer_date || t.created_at).toLocaleDateString('tr-TR')}</TableCell>
                <TableCell className="font-medium text-[#1a1916]">{t.from_warehouse_name}</TableCell>
                <TableCell><ArrowRight size={14} className="text-[#8a7e70]" /></TableCell>
                <TableCell className="font-medium text-[#1a1916]">{t.to_warehouse_name}</TableCell>
                <TableCell className="text-right text-[#3F3E39]">{t.items?.length || 0}</TableCell>
                <TableCell>
                  <span className={`status-badge ${transferStatusColors[t.status] || 'bg-[#e8dfd0] text-[#3F3E39]'}`}>
                    {transferStatusLabels[t.status] || t.status}
                  </span>
                  {/* Transfer İKİ onayla tamamlanır (çıkış + giriş deposu).
                      Hangi tarafın beklendiği yazılmazsa "Depo Onayı Bekliyor"
                      rozeti tek onay varmış izlenimi veriyor. */}
                  {t.bekleyen_onaylar?.length > 0 && (
                    <span className="block text-[11px] text-[#8a7e70] mt-1">
                      bekleyen onay: {t.bekleyen_onaylar.join(' + ')}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{t.created_by_name}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    {t.status === 'pending_warehouse' && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => handleApproveWarehouse(t.id)}
                          className="text-[#3d6e4e] hover:bg-[#3d6e4e]/10 text-xs h-7" data-testid={`approve-wh-${t.id}`}>
                          <CheckCircle size={14} className="mr-1" /> Onayla
                        </Button>
                        <Button size="sm" variant="ghost" 
                          onClick={() => { setSelectedTransfer(t); setShowRejectDialog(true); }}
                          className="text-[#7a3535] hover:bg-[#7a3535]/10 text-xs h-7" data-testid={`reject-${t.id}`}>
                          <XCircle size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {transfers.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#8a7e70]">Transfer bulunamadi</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {showNew && (
        <TransferForm
          onClose={() => setShowNew(false)}
          onComplete={() => { setShowNew(false); fetchTransfers(); }}
        />
      )}

      {/* Transfer Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
              <Repeat size={18} /> Transfer Detayi
            </DialogTitle>
          </DialogHeader>
          {selectedTransfer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#8a7e70]">Cikis Deposu</p>
                  <p className="font-semibold text-[#1a1916]">{selectedTransfer.from_warehouse_name}</p>
                </div>
                <div>
                  <p className="text-xs text-[#8a7e70]">Giris Deposu</p>
                  <p className="font-semibold text-[#1a1916]">{selectedTransfer.to_warehouse_name}</p>
                </div>
              </div>
              {/* Transfer tarihi burada hiç gösterilmiyordu; "Onay Geçmişi"ndeki
                  kayıt anı tek tarih olduğu için geçmiş tarihli transferler
                  bugün yapılmış gibi okunuyordu. */}
              <div>
                <p className="text-xs text-[#8a7e70]">Transfer Tarihi</p>
                <p className="font-semibold text-[#1a1916]">
                  {new Date(selectedTransfer.transfer_date || selectedTransfer.created_at)
                    .toLocaleDateString('tr-TR')}
                  <span className="text-xs font-normal text-[#8a7e70] ml-2">
                    stok hareketi bu tarihe işlenir
                  </span>
                </p>
              </div>
              <div>
                <p className="text-xs text-[#8a7e70] mb-1">Durum</p>
                <span className={`status-badge ${transferStatusColors[selectedTransfer.status] || ''}`}>
                  {transferStatusLabels[selectedTransfer.status] || selectedTransfer.status}
                </span>
              </div>
              {/* Audit Trail */}
              <div className="border-t border-[#e8dfd0] pt-3 space-y-2">
                <p className="text-xs font-semibold text-[#8a7e70] uppercase">Onay Gecmisi</p>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <User size={12} className="text-[#8a7e70]" />
                    <span className="text-[#8a7e70]">Olusturan:</span>
                    <span className="text-[#1a1916]">{selectedTransfer.created_by_name}</span>
                    <span className="text-xs text-[#8a7e70]">
                      ({new Date(selectedTransfer.created_at).toLocaleString('tr-TR')})
                    </span>
                  </div>
                  {/* İKİ ayrı onay gösterilir. Tek "Onaylayan" satırı, çift onay
                      kuralında hangi tarafın imzasının eksik olduğunu gizliyordu. */}
                  {[
                    { etiket: 'Çıkış deposu', ad: selectedTransfer.from_approved_by_name,
                      an: selectedTransfer.from_approved_at, depo: selectedTransfer.from_warehouse_name },
                    { etiket: 'Giriş deposu', ad: selectedTransfer.to_approved_by_name,
                      an: selectedTransfer.to_approved_at, depo: selectedTransfer.to_warehouse_name },
                  ].map(o => (
                    <div key={o.etiket} className="flex items-center gap-2">
                      {o.ad
                        ? <CheckCircle size={12} className="text-[#3d6e4e]" />
                        : <Clock size={12} className="text-[#7a6a2e]" />}
                      <span className="text-[#8a7e70]">{o.etiket} ({o.depo}):</span>
                      {o.ad ? (
                        <>
                          <span className="text-[#1a1916]">{o.ad}</span>
                          {o.an && (
                            <span className="text-xs text-[#8a7e70]">
                              ({new Date(o.an).toLocaleString('tr-TR')})
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-[#7a6a2e]">onay bekliyor</span>
                      )}
                    </div>
                  ))}
                  {selectedTransfer.rejected_by_name && (
                    <div className="flex items-center gap-2">
                      <XCircle size={12} className="text-[#7a3535]" />
                      <span className="text-[#8a7e70]">Reddeden:</span>
                      <span className="text-[#1a1916]">{selectedTransfer.rejected_by_name}</span>
                      {selectedTransfer.rejection_reason && (
                        <span className="text-xs text-[#7a3535]">({selectedTransfer.rejection_reason})</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {/* Items */}
              {selectedTransfer.items?.length > 0 && (
                <div className="border-t border-[#e8dfd0] pt-3">
                  <p className="text-xs font-semibold text-[#8a7e70] uppercase mb-2">Urunler ({selectedTransfer.items.length})</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {selectedTransfer.items.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b border-[#e8dfd0] last:border-b-0">
                        <span className="text-[#1a1916]">{item.product_name}</span>
                        <span className="font-mono text-[#3F3E39]">{trSayi(item.quantity)} {item.unit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetail(false)} className="border-[#d0c4b0]">Kapat</Button>
            {selectedTransfer?.status === 'pending_warehouse' && (
              <>
                <Button onClick={() => { setShowRejectDialog(true); }} variant="outline" 
                  className="border-[#7a3535] text-[#7a3535] hover:bg-[#7a3535]/10">
                  <XCircle size={14} className="mr-1" /> Reddet
                </Button>
                <Button onClick={() => handleApproveWarehouse(selectedTransfer.id)} disabled={actionLoading}
                  className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]">
                  <CheckCircle size={14} className="mr-1" /> Onayla ve Tamamla
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
              <XCircle size={18} className="text-[#7a3535]" /> Transferi Reddet
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Red Nedeni</Label>
              <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Red nedeninizi yazin..."
                className="bg-white border-[#d0c4b0] min-h-[80px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectDialog(false); setRejectReason(''); }} 
              className="border-[#d0c4b0]">Iptal</Button>
            <Button onClick={handleReject} disabled={actionLoading}
              className="bg-[#7a3535] text-white hover:bg-[#5a2525]">
              {actionLoading ? 'Isleniyor...' : 'Reddet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── TRANSFER FORM ───
function TransferForm({ onClose, onComplete }) {
  const [warehouses, setWarehouses] = useState([]);
  const [fromWh, setFromWh] = useState('');
  const [toWh, setToWh] = useState('');
  const [notes, setNotes] = useState('');
  // Transferin IS TARIHI. Varsayilan bugun; gecmis bir gun secilebilir.
  // Stok hareketi bu tarihe yazilir (sunucuda `movement_date`), onay gunune
  // degil — dun yapilan transfer raporda dune dusmeli.
  const [transferDate, setTransferDate] = useState(
    () => new Date().toLocaleDateString('en-CA'));   // yerel gun, YYYY-MM-DD
  const [items, setItems] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sourceStock, setSourceStock] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    api.get('/warehouses').then(r => setWarehouses(r.data)).catch(() => {});
  }, []);

  // Load source warehouse stock when fromWh changes
  useEffect(() => {
    if (fromWh) {
      api.get(`/warehouses/${fromWh}/stock`).then(r => setSourceStock(r.data)).catch(() => setSourceStock([]));
    } else {
      setSourceStock([]);
    }
    setItems([]);
  }, [fromWh]);

  // Search products in source stock
  useEffect(() => {
    if (search.length < 2 || !sourceStock.length) { setSearchResults([]); return; }
    // Karşılaştırmanın İKİ tarafı da AYNI normalleştirmeden geçmeli —
    // bkz. TransferList.js'teki aynı düzeltme. Sorgu `toLowerCase()`, ürün adı
    // `trNorm()` ile normallenince Türkçe harfli ürünler hiç bulunamıyordu.
    const sl = trNorm(search);
    const existing = new Set(items.map(i => i.product_card_id));
    setSearchResults(
      sourceStock.filter(s =>
        !existing.has(s.product_card_id) &&
        (trNorm(s.product_name).includes(sl) || trNorm(s.product_code).includes(sl))
      ).slice(0, 8)
    );
  }, [search, sourceStock, items]);

  const addProduct = (stockItem) => {
    setItems(prev => [...prev, {
      product_card_id: stockItem.product_card_id,
      product_name: stockItem.product_name,
      product_code: stockItem.product_code,
      unit: stockItem.product_unit || stockItem.unit,
      available: stockItem.quantity,
      quantity: ''
    }]);
    setSearch('');
    setSearchResults([]);
  };

  const updateQty = (i, val) => {
    const updated = [...items];
    updated[i] = { ...updated[i], quantity: val };
    setItems(updated);
  };

  const removeItem = (i) => setItems(prev => prev.filter((_, j) => j !== i));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post('/transfers', {
        from_warehouse_id: fromWh,
        to_warehouse_id: toWh,
        notes,
        transfer_date: transferDate,
        items: items.map(i => ({
          product_card_id: i.product_card_id,
          quantity: parseFloat(i.quantity),
          unit: i.unit
        }))
      });
      toast.success('Transfer tamamlandi');
      setShowConfirm(false);
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata olustu');
    }
    setLoading(false);
  };

  const validItems = items.filter(i => i.quantity && parseFloat(i.quantity) > 0);
  const fromName = warehouses.find(w => w.id === fromWh)?.name || '';
  const toName = warehouses.find(w => w.id === toWh)?.name || '';

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
            <Repeat size={20} className="text-[#2e2d29]" /> Yeni Transfer
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* Warehouse selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Cikis Deposu *</Label>
              <Select value={fromWh} onValueChange={setFromWh}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="transfer-from-wh">
                  <SelectValue placeholder="Depo secin" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.filter(w => w.id !== toWh).map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Giris Deposu *</Label>
              <Select value={toWh} onValueChange={setToWh}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="transfer-to-wh">
                  <SelectValue placeholder="Depo secin" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.filter(w => w.id !== fromWh).map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Product search */}
          {fromWh && toWh && (
            <>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Urun ara (cikis deposundaki stoktan)..."
                  className="pl-9 bg-white border-[#d0c4b0]" data-testid="transfer-search" />
              </div>
              {searchResults.length > 0 && (
                <div className="border border-[#d0c4b0] rounded-lg bg-white overflow-hidden">
                  {searchResults.map(s => (
                    <button key={s.product_card_id} onClick={() => addProduct(s)}
                      className="w-full flex items-center justify-between p-3 hover:bg-[#e8dfd0]/50 transition-colors border-b border-[#d0c4b0] last:border-b-0 text-left"
                      data-testid={`transfer-add-${s.product_code}`}>
                      <div>
                        <p className="font-semibold text-[#1a1916]">{s.product_name}</p>
                        <p className="text-xs text-[#8a7e70] font-mono">{s.product_code}</p>
                      </div>
                      <p className="text-sm font-semibold text-[#3F3E39]">{s.quantity?.toFixed(1)} {s.product_unit || s.unit}</p>
                    </button>
                  ))}
                </div>
              )}

              {/* Items table */}
              {items.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                      <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Urun</TableHead>
                      <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Mevcut</TableHead>
                      <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-28">Miktar</TableHead>
                      <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Birim</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, i) => {
                      const qty = parseFloat(item.quantity) || 0;
                      const over = qty > item.available;
                      return (
                        <TableRow key={i} className={`border-[#d0c4b0] ${over ? 'bg-[#7a3535]/5' : ''}`}>
                          <TableCell>
                            <p className="font-medium text-[#1a1916]">{item.product_name}</p>
                            <p className="text-xs text-[#8a7e70] font-mono">{item.product_code}</p>
                          </TableCell>
                          <TableCell className="text-right text-sm text-[#8a7e70]">{item.available?.toFixed(1)}</TableCell>
                          <TableCell>
                            {/* step="any": olculen deger; sabit adim tarayicida
                                "adim uyusmazligi" yapip formu sessizce engelliyor */}
                            <Input type="number" step="any" value={item.quantity}
                              onChange={(e) => updateQty(i, e.target.value)}
                              className={`bg-white border-[#d0c4b0] text-center ${over ? 'border-[#7a3535]' : ''}`}
                              data-testid={`transfer-qty-${i}`} />
                            {over && <p className="text-xs text-[#7a3535] mt-1">Stok yetersiz</p>}
                          </TableCell>
                          <TableCell className="text-sm text-[#8a7e70]">{item.unit}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" onClick={() => removeItem(i)}
                              className="h-8 w-8 text-[#8a7e70] hover:text-[#7a3535]" data-testid={`transfer-remove-${i}`}>
                              <X size={14} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </>
          )}

          {/* Transfer tarihi — varsayilan bugun, degistirilebilir */}
          <div className="space-y-2">
            <Label className="text-[#3F3E39] font-medium">Transfer Tarihi</Label>
            <Input type="date" value={transferDate}
              onChange={(e) => setTransferDate(e.target.value)}
              max={new Date().toLocaleDateString('en-CA')}
              className="bg-white border-[#d0c4b0] w-full sm:w-52"
              data-testid="transfer-date" />
            <p className="text-xs text-[#8a7e70]">
              Stok hareketi bu tarihe işlenir. Varsayılan bugündür.
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-[#3F3E39] font-medium">Notlar</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Transfer notu..." className="bg-white border-[#d0c4b0] min-h-[60px]" data-testid="transfer-notes" />
          </div>
        </div>

        <DialogFooter className="border-t border-[#d0c4b0] pt-4">
          <Button variant="outline" onClick={onClose} className="border-[#d0c4b0]">Iptal</Button>
          <Button onClick={() => setShowConfirm(true)}
            disabled={!fromWh || !toWh || validItems.length === 0 || items.some(i => parseFloat(i.quantity) > i.available)}
            className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="transfer-submit-btn">
            Transferi Tamamla
          </Button>
        </DialogFooter>

        {/* Confirm */}
        <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
          <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
            <DialogHeader>
              <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
                <AlertTriangle size={20} className="text-[#7a6a2e]" /> Transfer Onayi
              </DialogTitle>
              <DialogDescription className="text-[#3F3E39] pt-3 text-sm leading-relaxed">
                <span className="font-semibold text-[#1a1916]">{validItems.length} urun</span>{' '}
                <span className="font-semibold text-[#7a3535]">{fromName}</span>'dan{' '}
                <span className="font-semibold text-[#3d6e4e]">{toName}</span>'a transfer edilecek.
                <br /><br />
                Onayliyor musunuz?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirm(false)} className="border-[#d0c4b0]">Iptal</Button>
              <Button onClick={handleSubmit} disabled={loading}
                className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] font-semibold" data-testid="transfer-confirm-btn">
                {loading ? 'Isleniyor...' : 'Onayla'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
